import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout } from './components/layout/app-layout';
import { UnifiedCreditControlDashboard, CreditControlDashboard, CreditControlDashboardV2, CreditControlDashboardV3 } from './pages/dashboard';
import { AuthProvider, OnboardingProvider } from './contexts';
import { FilterProvider } from './contexts/filter-context';
import { OnboardingGate } from './components/onboarding/onboarding-gate';
import { ModeProvider } from './providers/ModeProvider';
import { useUserContext } from './hooks/useUserContext';
import { LoadingScreen } from './components/LoadingScreen';
import { CustomersPage } from './pages/customers';
import { CustomerDetailPage } from './pages/customer-detail';
import { TransactionsPage } from './pages/transactions';
import {
  PaymentsPage,
  ReceiptsDashboard,
  AllocationWorkspace,
  RecordPaymentPage,
  PaymentHistoryPage,
  UnappliedPaymentsPage,
  PaymentExceptionsPage,
  DirectDebitsPage,
  ReconciliationPage
} from './pages/payments';
import ReceiptsPage from './pages/payments/receipts';
import OCRRemittancePage from './pages/payments/ocr-remittance';
import DirectDebitsPageNew from './pages/payments/direct-debits';
import CardPaymentsPage from './pages/payments/card-payments';
import CollectionsDashboard from './pages/collections/dashboard';
import CollectionsQueue from './pages/collections/queue';
import CaseDetailsPage from './pages/collections/case-details';
import CreateCasePage from './pages/collections/create-case';
import PromisesPage from './pages/collections/promises';
import PromiseDetailsPage from './pages/collections/promise-details';
import CreatePromisePage from './pages/collections/create-promise';
import AgeingAnalysisPage from './pages/collections/ageing-analysis';
import { MultiCompanyDashboard } from './pages/analytics-reporting';
import CompanyDetailsPage from './pages/analytics-reporting/company-details';
import DisputesDashboardPage from './pages/disputes/dashboard';
import DisputesAllPage from './pages/disputes/all';
import DisputeDetailsPage from './pages/disputes/dispute-details';
import { CreditScoringPage } from './pages/credit-scoring';
import { SettingsLayout, CompanySettingsPage, DocumentTemplatesPage, TemplateEditorPage, StrategiesPage, StrategyEditorPage, UsersTabsPage, AIConfigurationPage, RoleDetailsPage, CreateRolePage, DuplicateRolePage, GeneralSettingsPage, ScreenDataModesPage } from './pages/settings';
import DisputeTypesPage from './pages/settings/dispute-types';
import CreateDisputeTypePage from './pages/settings/create-dispute-type-page';
import WorkflowEditorPage from './pages/settings/workflow-editor-page';
import { UsersPage } from './pages/users';
import { IntegrationsPage } from './pages/integrations';
import { NavigationMenuDemoPage } from './pages/examples/navigation-menu-demo';
import IconPreview from './pages/icon-preview';
import TaskBoardPage from './pages/tasks';

function AppContent() {
  const { isLoading } = useUserContext();

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <OnboardingProvider>
      <OnboardingGate />
      <Routes>
        <Route path="/" element={<AppLayout />}>
          {/* Unified Dashboard - Main Route */}
          <Route index element={<UnifiedCreditControlDashboard />} />
          
          {/* Legacy Dashboard Routes - Keep for reference/testing */}
          <Route path="dashboard-v1" element={<CreditControlDashboard />} />
          <Route path="dashboard-v2" element={<CreditControlDashboardV2 />} />
          <Route path="dashboard-v3" element={<CreditControlDashboardV3 />} />
          
          {/* Redirect old routes to unified dashboard */}
          <Route path="dashboard" element={<Navigate to="/" replace />} />
          
          {/* Task Board Route */}
          <Route path="tasks" element={<TaskBoardPage />} />
          
          <Route path="customers" element={<CustomersPage />} />
          <Route path="customers/:customerId" element={<CustomerDetailPage />} />
          <Route path="transactions" element={<TransactionsPage />} />

          {/* Payments Module Routes */}
          <Route path="payments" element={<PaymentsPage />} />
          <Route path="payments/receipts" element={<ReceiptsPage />} />
          <Route path="payments/ocr-remittance" element={<OCRRemittancePage />} />
          <Route path="payments/direct-debits" element={<DirectDebitsPageNew />} />
          <Route path="payments/card-payments" element={<CardPaymentsPage />} />
          <Route path="payments/allocate/:receiptId" element={<AllocationWorkspace />} />
          <Route path="payments/record" element={<RecordPaymentPage />} />
          <Route path="payments/history" element={<PaymentHistoryPage />} />
          <Route path="payments/unapplied" element={<UnappliedPaymentsPage />} />
          <Route path="payments/exceptions" element={<PaymentExceptionsPage />} />
          <Route path="payments/reconciliation" element={<ReconciliationPage />} />

          {/* Collections Module Routes */}
          <Route path="collections/dashboard" element={<CollectionsDashboard />} />
          <Route path="collections/queue" element={<CollectionsQueue />} />
          <Route path="collections/queue/case/:caseId" element={<CaseDetailsPage />} />
          <Route path="collections/queue/create" element={<CreateCasePage />} />
          <Route path="collections/promises" element={<PromisesPage />} />
          <Route path="collections/promises/create" element={<CreatePromisePage />} />
          <Route path="collections/promises/:promiseId" element={<PromiseDetailsPage />} />

          {/* Analytics & Reporting Module Routes */}
          <Route path="analytics-reporting/multi-company" element={<MultiCompanyDashboard />} />
          <Route path="analytics-reporting/multi-company/company/:companyId" element={<CompanyDetailsPage />} />
          <Route path="analytics-reporting/ageing-analysis" element={<AgeingAnalysisPage />} />

          {/* Dispute Management Routes */}
          <Route path="disputes" element={<DisputesDashboardPage />} />
          <Route path="disputes/all" element={<DisputesAllPage />} />
          <Route path="disputes/all/:disputeId" element={<DisputeDetailsPage />} />

          {/* Credit Scoring & Risk Assessment Route */}
          <Route path="credit-scoring" element={<CreditScoringPage />} />

          {/* Settings Module Routes */}
          <Route path="settings" element={<SettingsLayout />}>
            <Route path="company" element={<CompanySettingsPage />} />
            <Route path="general" element={<GeneralSettingsPage />} />
            <Route path="templates" element={<DocumentTemplatesPage />} />
            <Route path="strategies" element={<StrategiesPage />} />
            <Route path="dispute-types" element={<DisputeTypesPage />} />
            <Route path="users" element={<UsersTabsPage />} />
            <Route path="ai-configuration" element={<AIConfigurationPage />} />
            <Route path="screen-data-modes" element={<ScreenDataModesPage />} />
          </Route>

          {/* Template Editor - Full Screen Route (outside SettingsLayout) */}
          <Route path="settings/templates/edit" element={<TemplateEditorPage />} />
          <Route path="settings/templates/create" element={<TemplateEditorPage />} />

          {/* Strategy Editor - Full Screen Route (outside SettingsLayout) */}
          <Route path="settings/strategies/:strategyId" element={<StrategyEditorPage />} />
          <Route path="settings/strategies/edit" element={<StrategyEditorPage />} />
          <Route path="settings/strategies/create" element={<StrategyEditorPage />} />

          {/* Create Dispute Type - Full Screen Route (outside SettingsLayout) */}
          <Route path="settings/dispute-types/create" element={<CreateDisputeTypePage />} />

          {/* Workflow Editor - Full Screen Route (outside SettingsLayout) */}
          <Route path="settings/dispute-types/:disputeTypeId/workflow" element={<WorkflowEditorPage />} />

          {/* Role Details - Full Screen Route (outside SettingsLayout) */}
          <Route path="settings/users/roles/:roleId" element={<RoleDetailsPage />} />
          <Route path="settings/users/roles-permissions/new" element={<CreateRolePage />} />
          <Route path="settings/users/roles-permissions/duplicate/:roleId" element={<DuplicateRolePage />} />

          {/* User Management Route */}
          <Route path="users" element={<UsersPage />} />

          {/* System Integrations Route */}
          <Route path="integrations" element={<IntegrationsPage />} />

          {/* Examples Route */}
          <Route path="examples/navigation-menu" element={<NavigationMenuDemoPage />} />
          <Route path="examples/icon-preview" element={<IconPreview />} />
        </Route>
          </Routes>
          </OnboardingProvider>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <FilterProvider>
        <BrowserRouter>
          <ModeProvider>
            <AppContent />
          </ModeProvider>
        </BrowserRouter>
      </FilterProvider>
    </AuthProvider>
  );
}

export default App;
